// Ubicación en el repo: src/components/BranchNotes.jsx
// Cartelera de avisos de la sucursal (DECISIONS.md #44), en el Dashboard.
// Mismo patrón que PatientAttachments.jsx: componente autocontenido que
// carga su propia lista. Solo se ven notas de los últimos 7 días
// (WINDOW_DAYS en src/lib/branchNotes.js). Puede borrar el autor o un
// Admin — el botón "Eliminar" solo se muestra en ese caso, y RLS lo exige
// igual del lado del servidor.
import { useEffect, useState } from 'react';
import { listBranchNotes, createBranchNote, softDeleteBranchNote } from '../lib/branchNotes';
import { formatDateTime } from '../lib/format';
import Spinner from './Spinner';

export default function BranchNotes({ branchId, professional }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      setNotes(await listBranchNotes(branchId));
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los avisos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setPosting(true);
    setError('');
    try {
      await createBranchNote(branchId, message, professional.id);
      setMessage('');
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo publicar el aviso.');
    } finally {
      setPosting(false);
    }
  }

  async function handleDelete(note) {
    const ok = window.confirm('¿Eliminar este aviso?');
    if (!ok) return;
    setBusyId(note.id);
    setError('');
    try {
      await softDeleteBranchNote(note.id, professional.id);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el aviso.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="detail-section">
      <h2>📌 Avisos de la sucursal</h2>
      <p className="text-muted" style={{ marginTop: -6 }}>
        Visibles durante 7 días. Cualquier profesional con acceso a esta sucursal puede publicar.
      </p>

      {error && <p className="auth-error">{error}</p>}

      <form onSubmit={handleSubmit} className="branch-notes-form">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Escribe un aviso para el equipo…"
          rows={3}
          disabled={posting}
        />
        <div className="branch-notes-form__actions">
          <button type="submit" className="button-primary" disabled={posting || !message.trim()}>
            {posting ? 'Publicando…' : 'Publicar'}
          </button>
        </div>
      </form>

      {loading ? (
        <Spinner />
      ) : notes.length === 0 ? (
        <p className="text-muted">Sin avisos en los últimos 7 días.</p>
      ) : (
        <div className="branch-notes-grid">
          {notes.map((note) => {
            const canDelete = professional.is_admin || note.created_by === professional.id;
            const author = note.professionals
              ? `${note.professionals.nombre} ${note.professionals.apellido}`
              : 'Alguien';
            return (
              <div className="branch-note-card" key={note.id}>
                <div className="branch-note-card__message">{note.message}</div>
                <div className="branch-note-card__meta">
                  {author} · {formatDateTime(note.created_at)}
                </div>
                {canDelete && (
                  <button
                    type="button"
                    className="link-button link-button--danger branch-note-card__delete"
                    disabled={busyId === note.id}
                    onClick={() => handleDelete(note)}
                  >
                    Eliminar
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
